import {
    GatewayByIdResponse,
} from 'app/requests/Gateway/Gateway.types'
import {
    DTOCardFormField,
} from 'app/types/form.types'
import {
    LocationType,
    GatewayDataPlan,
    GatewayLocationCategory,
    GatewayStatus,
    GatewayStorageArea,
    GatewayType,
    GatewayMainType,
    GatewayHardwareVersion,
    GatewaySoftwareVersion,
} from 'app/enums/label.enums'

const getFields = ({
    gatewayStatus,
    showConnectedLogger = true,
    showConnectionInfo = true,
    getAllowedValues,
    locationId = undefined,
    disabled = false,
}: {
    showConnectedLogger: boolean,
    showConnectionInfo: boolean,
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    locationId: number,
    disabled: boolean,
    gatewayStatus?: GatewayStatus,
}): DTOCardFormField<keyof GatewayByIdResponse>[] => {
    return [
        {
            id: 'gateway',
            title: 'Gateway',
            className: 'gateway',
            fields: [
                {
                    id: 'column1',
                    fields: [
                        {
                            name: 'gatewayImeiMac',
                            componentName: 'InputSingleline',
                            labelKey: 'GATEWAY_IMEI_MAC_ADDRESS',
                        },
                        {
                            name: 'gatewaySimCardNumber',
                            componentName: 'InputSingleline',
                            labelKey: 'GATEWAY_SIM_CARD_NUMBER',
                        },
                        {
                            name: 'gatewaySimCardNumberBackup',
                            componentName: 'InputSingleline',
                            labelKey: 'GATEWAY_SIM_CARD_NUMBER_BACKUP',
                        },
                        {
                            name: 'hardwareVersion',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayHardwareVersion),
                            useDropdown: true,
                            required: gatewayStatus !== GatewayStatus.DECOMMISSIONED,
                            labelKey: 'HARDWARE_VERSION',
                        },
                        {
                            name: 'softwareVersion',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewaySoftwareVersion),
                            useDropdown: true,
                            required: gatewayStatus !== GatewayStatus.DECOMMISSIONED,
                            labelKey: 'SOFTWARE_VERSION',
                        },
                        {
                            name: 'installedOn',
                            componentName: 'DateTimeSelect',
                            required: gatewayStatus === GatewayStatus.INSTALLED,
                            labelKey: 'INSTALLED_ON',
                        },
                        {
                            name: 'installedBy',
                            componentName: 'ContactObjectSelector',
                            labelKey: 'INSTALLED_BY',
                        },
                        {
                            name: 'supportingDocuments',
                            componentName: 'AttachmentsObjectSelector',
                            labelKey: 'SUPPORTING_DOCUMENTS',
                        },
                    ],
                },
                {
                    id: 'column2',
                    fields: [
                        {
                            name: 'gatewayMainType',
                            className: 'gatewayType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayMainType),
                            useDropdown: true,
                            disabled: true,
                            labelKey: 'GATEWAY_MAIN_TYPE',
                        },
                        {
                            name: 'gatewayType',
                            className: 'gatewayType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayType),
                            useDropdown: true,
                            labelKey: 'GATEWAY_TYPE',
                        },
                        {
                            name: 'gatewayConnectionType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayDataPlan),
                            useDropdown: true,
                            required: true,
                            labelKey: 'CONNECTION_TYPE',
                        },
                        {
                            name: 'gatewayStatus',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayStatus),
                            useDropdown: true,
                            required: true,
                            labelKey: 'GATEWAY_STATUS',
                        },
                        {
                            name: 'rolledOutFor',
                            componentName: 'DeploymentSelector',
                            required: true,
                            labelKey: 'ROLLED_OUT_FOR',
                        },
                        {
                            name: 'usedBy',
                            componentName: 'MultipleDeploymentSelector',
                            labelKey: 'USED_BY',
                        },
                        {
                            name: 'remark',
                            className: 'remark',
                            componentName: 'LongText',
                            labelKey: 'REMARK',
                        },
                    ],
                },
            ],
        },
        {
            id: 'location',
            title: 'Location',
            className: 'location',
            fields: [
                {
                    id: 'column1',
                    fields: [
                        {
                            name: 'location',
                            componentName: 'LocationObjectDataSelector',
                            filterLocationType: LocationType.ADDRESS,
                            isShippingLocation: true,
                            required: true,
                            labelKey: 'LOCATION',
                        },
                        {
                            name: 'area',
                            required: gatewayStatus === GatewayStatus.TO_BE_INSTALLED
                                || gatewayStatus === GatewayStatus.INSTALLED,
                            componentName: 'AreaSelector',
                            locationId,
                            labelKey: 'AREA',
                        },
                        {
                            name: 'locationRemark',
                            componentName: 'InputSingleline',
                            labelKey: 'LOCATION_REMARK',
                        },
                        {
                            name: 'gatewayStorageArea',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(GatewayStorageArea),
                            labelKey: 'STORAGE_AREA',
                        },
                    ],
                },
                {
                    id: 'column2',
                    fields: [
                        {
                            name: 'latitude',
                            componentName: 'InputSingleline',
                            required: true,
                            labelKey: 'LATITUDE',
                        },
                        {
                            name: 'longitude',
                            componentName: 'InputSingleline',
                            required: true,
                            labelKey: 'LONGITUDE',
                        },
                        {
                            name: 'gatewayLocationCategories',
                            componentName: 'MultiEnumSelector',
                            disabled: true,
                            allowedValues: getAllowedValues(GatewayLocationCategory),
                            labelKey: 'CATEGORY',
                        },
                    ],
                },
            ],
        },
        {
            title: 'Connection Information',
            className: 'connectionInfo',
            id: 'connectionInfo',
            hidden: !showConnectionInfo,
            fields: [{
                name: 'lastConnectedOn',
                disabled: true,
                componentName: 'TemperatureAge',
                labelKey: 'LAST_CONNECTION',
            }],
        },
        {
            title: 'Gateway Contacts',
            className: 'contacts',
            id: 'contacts',
            fields: [disabled ? {
                name: 'gatewayContacts',
                componentName: 'GatewayContacts',
            } : {
                name: 'gatewayContacts',
                componentName: 'MultipleContactObjectSelector',
                required: gatewayStatus === GatewayStatus.TO_BE_INSTALLED
                    || gatewayStatus === GatewayStatus.INSTALLED,
                value: [],
            }],
        },
        {
            title: 'Connected Loggers',
            className: 'loggers',
            id: 'loggers',
            hidden: !showConnectedLogger,
            fields: [{
                name: 'id',
                componentName: 'ConnectedLoggers',
            }],
        },
    ]
}

export default getFields
